import { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Checkbox, Group, Loader, Modal, Select, Stack, Table,
  Text, TextInput, Textarea, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconChevronRight, IconPlus, IconTable } from '@tabler/icons-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCampaigns, useCreateCampaign } from '../api/queries';
import { useVocab } from '../vocab';
import { DoesChips, rowTypeIsAboutAPerson, useRowTypes } from './streams/streamShared';
import { visiblePaths } from '../layout/commands';
import { useNavUser } from '../layout/navUser';

/**
 * Name and what arrives, and everything else afterwards.
 *
 * Routing, custom fields, dedupe and suppression all live on the campaign's own
 * page and can be changed at any time. Asking for them here would put a form in
 * front of the one thing this dialog is for, and every answer would be a guess
 * made before the campaign exists. The row type is the exception: a stream of
 * orders or bookings starts with the duplicate check off (the server decides,
 * from the type), since there is no person to check.
 */
function NewCampaignModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isReferenceList, setIsReferenceList] = useState(false);
  const [rowType, setRowType] = useState('lead');
  const rowTypes = useRowTypes();
  const create = useCreateCampaign();
  const v = useVocab();
  const navigate = useNavigate();

  const close = () => {
    create.reset();
    setName('');
    setDescription('');
    setIsReferenceList(false);
    setRowType('lead');
    onClose();
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const res = await create.mutateAsync({
        name: trimmed,
        description: description.trim() || undefined,
        is_reference_list: isReferenceList,
        ...(isReferenceList ? {} : { row_type: rowType }),
      });
      close();
      notifications.show({ message: `Created ${trimmed}`, color: 'green' });
      // Straight to the campaign: a new one has no destinations and cannot
      // deliver anything, so the next step is always to set it up.
      navigate(`/campaigns/${res.campaign_id}`);
    } catch {
      // The error is rendered in the dialog; the dialog stays open so what was
      // typed is not lost.
    }
  };

  return (
    <Modal opened={opened} onClose={close} title={`New ${v.one('campaign')}`} size="md">
      <Stack>
        {create.error && (
          <Alert color="red" variant="light">{create.error.message}</Alert>
        )}
        <TextInput
          label="Name"
          placeholder="Energy Premium"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          data-autofocus
          required
        />
        <Textarea
          label="Description"
          description={`Optional. What this ${v.one('campaign')} is for.`}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          autosize
          minRows={2}
        />
        {!isReferenceList && (
          <Select
            label="What arrives"
            description={rowTypeIsAboutAPerson(rowType)
              ? `A label for its ${v.many('lead')}, shown in lists. It can be changed later.`
              : `Not about a person, so it starts with the duplicate check off. Suppression and phone checks start off on every new ${v.one('campaign')}.`}
            data={rowTypes.options}
            value={rowType}
            onChange={(val) => setRowType(val ?? 'lead')}
            allowDeselect={false}
            data-testid="new-row-type"
          />
        )}
        <Checkbox
          label="Reference list"
          description={`Holds match values rather than ${v.many('lead')}, and is kept out of dashboards and reports.`}
          checked={isReferenceList}
          onChange={(e) => setIsReferenceList(e.currentTarget.checked)}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={close}>Cancel</Button>
          <Button
            onClick={submit}
            loading={create.isPending}
            disabled={!name.trim()}
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function CampaignsPage() {
  const { data, isPending, error } = useCampaigns();
  const [creating, setCreating] = useState(false);
  const v = useVocab();
  const rowTypes = useRowTypes();
  // Imports kept their place on the menu; they are also one click from here,
  // as a file is one of the ways rows arrive on a stream.
  const user = useNavUser();
  const seesImports = !!user && visiblePaths(user).has('/sheets');
  const navigate = useNavigate();
  /* /campaigns?new=1, from the command bar: open the new form, then drop the
     param so a reload or Back does not open it again. */
  const [params, setParams] = useSearchParams();
  const wantsNew = params.get('new') === '1';
  useEffect(() => {
    if (!wantsNew) return;
    setCreating(true);
    setParams((p) => { p.delete('new'); return p; }, { replace: true });
  }, [wantsNew, setParams]);

  return (
    <Stack>
      <Group justify="space-between" align="center">
        <div>
          <Title order={2}>{v.Many('campaign')}</Title>
          <Text size="sm" c="dimmed">
            {`Where ${v.many('lead')} arrive, and what happens to them: sold, assigned, sent on, or kept.`}
          </Text>
        </div>
        <Group gap="sm">
          {seesImports && (
            <Button variant="default" leftSection={<IconTable size={16} />} component={Link} to="/sheets">
              Imports
            </Button>
          )}
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            {`New ${v.one('campaign')}`}
          </Button>
        </Group>
      </Group>
      {error && <Alert color="red" variant="light">{error.message}</Alert>}

      <NewCampaignModal opened={creating} onClose={() => setCreating(false)} />

      <Card p={0}>
        {isPending ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{v.One('campaign')}</Table.Th>
                  <Table.Th>What it does</Table.Th>
                  <Table.Th ta="right">{v.Many('route')}</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.campaigns.length === 0 && (
                  <Table.Tr><Table.Td colSpan={5}>
                    <Text ta="center" c="dimmed" py="lg">
                      {`No ${v.many('campaign')} yet. ${v.A('campaign')} is where ${v.many('lead')} arrive, by API or from a file; add one, then give it ${v.many('route')}.`}
                    </Text>
                  </Table.Td></Table.Tr>
                )}
                {data?.campaigns.map((c) => {
                  // A live stream with nowhere to send only stores; worth a
                  // flag in case that was not meant. The count comes from the
                  // same join routing uses.
                  const orphaned = c.active && !c.is_reference_list && c.destination_count === 0;
                  return (
                    <Table.Tr
                      key={c.id}
                      onClick={() => navigate(`/campaigns/${c.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td>
                        <Group gap="xs">
                          <Text size="sm" fw={500}>{c.name}</Text>
                          {c.is_reference_list && (
                            <Tooltip label={`Holds match values rather than ${v.many('lead')}, and is excluded from reporting`}>
                              <Badge size="sm" variant="outline" color="gray" style={{ cursor: 'help' }}>
                                reference list
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed" data-testid="row-type">
                          {c.is_reference_list ? 'Reference list' : rowTypes.label(c.row_type)}
                        </Text>
                      </Table.Td>
                      <Table.Td><DoesChips does={c.does} v={v} /></Table.Td>
                      <Table.Td ta="right">
                        <Group gap={4} justify="flex-end">
                          <Text size="sm">{c.destination_count}</Text>
                          {orphaned && (
                            <Tooltip label={`Active but has no ${v.many('route')}, so it only keeps what arrives`}>
                              <IconAlertTriangle size={15} color="var(--mantine-color-chartAmber-6)" />
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color={c.active ? 'chartGreen' : 'gray'}>
                          {c.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="right"><IconChevronRight size={16} opacity={0.4} /></Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
    </Stack>
  );
}
